import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../styles/constants';

interface CalendarProps {
  selectedStartDate?: Date | null;
  selectedEndDate?: Date | null;
  onDateSelect?: (date: Date) => void;
  onRangeSelect?: (startDate: Date, endDate: Date) => void;
  month: number; // 0-11
  year: number;
  onMonthChange?: (month: number, year: number) => void;
  showNavigation?: boolean;
  disabledDays?: number[]; // 0 = Sunday, 6 = Saturday
  minDate?: Date;
  maxDate?: Date;
  mode?: 'single' | 'range';
}

const Calendar: React.FC<CalendarProps> = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  onRangeSelect,
  month,
  year,
  onMonthChange,
  showNavigation = true,
  disabledDays = [5, 6], // Saturday and Sunday by default
  minDate,
  maxDate,
  mode = 'range',
}) => {
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  // Generate calendar dates
  const calendarDates = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;

    // Get previous month's dates to fill the first week
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const prevMonthDates = [];
    for (let i = adjustedStartDay - 1; i >= 0; i--) {
      prevMonthDates.push({
        date: prevMonthLastDay - i,
        month: month - 1,
        year: month === 0 ? year - 1 : year,
        isCurrentMonth: false,
      });
    }

    // Current month dates
    const currentMonthDates = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDates.push({
        date: i,
        month,
        year,
        isCurrentMonth: true,
      });
    }

    // Next month dates to fill the last week
    const totalCells = prevMonthDates.length + currentMonthDates.length;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    const nextMonthDates = [];
    for (let i = 1; i <= remainingCells; i++) {
      nextMonthDates.push({
        date: i,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false,
      });
    }

    return [...prevMonthDates, ...currentMonthDates, ...nextMonthDates];
  }, [month, year]);

  const handlePreviousMonth = () => {
    if (onMonthChange) {
      if (month === 0) {
        onMonthChange(11, year - 1);
      } else {
        onMonthChange(month - 1, year);
      }
    }
  };

  const handleNextMonth = () => {
    if (onMonthChange) {
      if (month === 11) {
        onMonthChange(0, year + 1);
      } else {
        onMonthChange(month + 1, year);
      }
    }
  };

  const isDateDisabled = (date: Date) => {
    const dayOfWeek = date.getDay();
    if (disabledDays.includes(dayOfWeek)) return true;
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isDateSelected = (date: Date) => {
    if (!selectedStartDate) return false;
    
    if (mode === 'single') {
      return (
        date.getDate() === selectedStartDate.getDate() &&
        date.getMonth() === selectedStartDate.getMonth() &&
        date.getFullYear() === selectedStartDate.getFullYear()
      );
    }

    // Range mode
    const dateTime = date.getTime();
    const startTime = selectedStartDate.getTime();
    
    if (selectedEndDate) {
      const endTime = selectedEndDate.getTime();
      return dateTime >= startTime && dateTime <= endTime;
    }

    // Only start date selected, check if hovering for preview
    if (hoveredDate && dateTime > startTime) {
      const hoverTime = hoveredDate.getTime();
      return dateTime >= startTime && dateTime <= hoverTime;
    }

    return dateTime === startTime;
  };

  const isDateInRange = (date: Date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    const dateTime = date.getTime();
    const startTime = selectedStartDate.getTime();
    const endTime = selectedEndDate.getTime();
    return dateTime > startTime && dateTime < endTime;
  };

  const isStartDate = (date: Date) => {
    if (!selectedStartDate) return false;
    return (
      date.getDate() === selectedStartDate.getDate() &&
      date.getMonth() === selectedStartDate.getMonth() &&
      date.getFullYear() === selectedStartDate.getFullYear()
    );
  };

  const isEndDate = (date: Date) => {
    if (!selectedEndDate) return false;
    return (
      date.getDate() === selectedEndDate.getDate() &&
      date.getMonth() === selectedEndDate.getMonth() &&
      date.getFullYear() === selectedEndDate.getFullYear()
    );
  };

  const handleDateClick = (dateInfo: { date: number; month: number; year: number; isCurrentMonth: boolean }) => {
    if (!dateInfo.isCurrentMonth) return;
    
    const clickedDate = new Date(dateInfo.year, dateInfo.month, dateInfo.date);
    
    if (isDateDisabled(clickedDate)) return;

    if (mode === 'single') {
      if (onDateSelect) {
        onDateSelect(clickedDate);
      }
      return;
    }

    // Range mode
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Starting a new range
      if (onDateSelect) {
        onDateSelect(clickedDate);
      }
    } else {
      // Completing the range
      if (clickedDate >= selectedStartDate) {
        if (onRangeSelect) {
          onRangeSelect(selectedStartDate, clickedDate);
        }
      } else {
        // Clicked date is before start, make it the new start
        if (onDateSelect) {
          onDateSelect(clickedDate);
        }
      }
    }
  };

  const handleDateHover = (dateInfo: { date: number; month: number; year: number; isCurrentMonth: boolean }) => {
    if (!dateInfo.isCurrentMonth) return;
    const date = new Date(dateInfo.year, dateInfo.month, dateInfo.date);
    if (!isDateDisabled(date)) {
      setHoveredDate(date);
    }
  };

  const getDayOfWeek = (dateInfo: { date: number; month: number; year: number }) => {
    const date = new Date(dateInfo.year, dateInfo.month, dateInfo.date);
    return date.getDay();
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%',
    }}>
      {/* Navigation Bar */}
      {showNavigation && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          height: '44px',
        }}>
          <IconButton
            onClick={handlePreviousMonth}
            sx={{
              backgroundColor: '#F6F8F8',
              borderRadius: '30px',
              width: 32,
              height: 32,
              '&:hover': { backgroundColor: '#E5E9E9' },
            }}
          >
            <ChevronLeft sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text }} />
          </IconButton>
          
          <Typography sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '16px',
            fontWeight: 400,
            color: DESIGN_TOKENS.colors.text,
          }}>
            {monthNames[month]} {year}
          </Typography>
          
          <Box sx={{ width: 32 }} /> {/* Spacer for left-aligned nav */}
        </Box>
      )}

      {showNavigation === false && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          height: '44px',
        }}>
          <Typography sx={{
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
            fontSize: '16px',
            fontWeight: 400,
            color: DESIGN_TOKENS.colors.text,
          }}>
            {monthNames[month]} {year}
          </Typography>
          
          <IconButton
            onClick={handleNextMonth}
            sx={{
              backgroundColor: '#F6F8F8',
              borderRadius: '30px',
              width: 32,
              height: 32,
              marginLeft: 'auto',
              '&:hover': { backgroundColor: '#E5E9E9' },
            }}
          >
            <ChevronRight sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text }} />
          </IconButton>
        </Box>
      )}

      {/* Week Days Header */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        padding: '6px 0',
        marginBottom: '8px',
      }}>
        {weekDays.map((day, index) => {
          const isDayOff = disabledDays.includes(index === 6 ? 0 : index + 1);
          return (
            <Box
              key={day + index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '32px',
              }}
            >
              <Typography sx={{
                fontFamily: DESIGN_TOKENS.typography.fontFamily,
                fontSize: '13px',
                fontWeight: 400,
                color: isDayOff ? 'rgba(146, 91, 100, 0.4)' : DESIGN_TOKENS.colors.textSecondary,
                width: '32px',
                textAlign: 'center',
              }}>
                {day}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 0,
        }}
        onMouseLeave={() => setHoveredDate(null)}
      >
        {calendarDates.map((dateInfo, index) => {
          const date = new Date(dateInfo.year, dateInfo.month, dateInfo.date);
          const isDisabled = isDateDisabled(date);
          const isSelected = isDateSelected(date);
          const isStart = isStartDate(date);
          const isEnd = isEndDate(date);
          const isInRange = isDateInRange(date);
          const dayOfWeek = getDayOfWeek(dateInfo);
          const isDayOff = disabledDays.includes(dayOfWeek);

          return (
            <Box
              key={`${dateInfo.year}-${dateInfo.month}-${dateInfo.date}-${index}`}
              onClick={() => handleDateClick(dateInfo)}
              onMouseEnter={() => handleDateHover(dateInfo)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '40px',
                cursor: dateInfo.isCurrentMonth && !isDisabled ? 'pointer' : 'default',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: isSelected && mode === 'range' ? (isStart || isEnd ? '20px' : '0px') : '20px',
                  backgroundColor: isSelected
                    ? DESIGN_TOKENS.colors.primary
                    : isInRange
                    ? 'rgba(103, 49, 79, 0.1)'
                    : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': dateInfo.isCurrentMonth && !isDisabled ? {
                    backgroundColor: isSelected ? DESIGN_TOKENS.colors.primary : '#F6F8F8',
                  } : {},
                }}
              >
                <Typography
                  sx={{
                    fontFamily: DESIGN_TOKENS.typography.fontFamily,
                    fontSize: '16px',
                    fontWeight: 400,
                    color: !dateInfo.isCurrentMonth
                      ? '#C5CBD3'
                      : isSelected
                      ? '#FFFFFF'
                      : isDayOff
                      ? '#925B64'
                      : isDisabled
                      ? DESIGN_TOKENS.colors.textSecondary
                      : DESIGN_TOKENS.colors.text,
                    userSelect: 'none',
                  }}
                >
                  {dateInfo.date}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Calendar;


